import React, { useEffect, useState, useCallback, useRef } from 'react';
import DownloadPdfButton from './DownloadPdfButton';
import DamagePdf from './DamagePdf';
import DamageImagePdf from './DamageImagePdf';
import type { VinInspectionItem } from '../../features/inspections';
import { useDamages } from '../../hooks/useDamages';
import { fetchProtectedMedia } from '../../utils/protectedMedia';

/* ---------------- types ---------------- */
interface PdfContainerProps {
  inspection: VinInspectionItem;
}

const PdfContainer: React.FC<PdfContainerProps> = ({ inspection }) => {
  const [, setDamage] = useState<number>(0);
  const [images, setImages] = useState<any[]>([]);
  const [imgLoading, setImgLoading] = useState<boolean>(false);
  const [imagesReady, setImagesReady] = useState<boolean>(false);

  // ✅ cache to prevent re-fetch
  const imageCache = useRef<Record<string, string>>({});

  const { filteredDamages, loading } = useDamages({
    inspectionId: inspection?.inspectionId,
    setDamage,
  });

  /* ---------------- Convert Image ---------------- */
  const convertToBase64 = useCallback(async (url: string) => {
    try {
      const response = await fetchProtectedMedia(url, {
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Image fetch failed: ${response.status}`);
      }

      const blob = await response.blob();

      return await new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Image fetch failed:', url);
      return null;
    }
  }, []);

  /* ---------------- Load Images ---------------- */
  const loadImages = useCallback(async () => {
    if (!filteredDamages?.length) {
      setImages([]);
      setImagesReady(true);
      return;
    }

    setImgLoading(true);
    setImagesReady(false);

    try {
      const updated = await Promise.all(
        filteredDamages.map(async item => {
          const imageURI = item.imageURI;
          if (!imageURI) {
            return item;
          }

          // ✅ use cache if available
          if (imageCache.current[imageURI]) {
            return {
              ...item,
              base64: imageCache.current[imageURI],
            };
          }

          // ✅ skip if already base64
          if (item.base64) return item;

          const base64 = await convertToBase64(imageURI);

          if (base64) {
            imageCache.current[imageURI] = base64;
          }

          return {
            ...item,
            base64,
          };
        })
      );

      const validImages = updated.filter(img => img?.base64);

      setImages(validImages);
    } catch (err) {
      console.error('Error loading images:', err);
    } finally {
      setImgLoading(false);
      setImagesReady(true); // ✅ ensures full completion
    }
  }, [filteredDamages, convertToBase64]);

  /* ---------------- Trigger Load ---------------- */
  useEffect(() => {
    loadImages();
  }, [loadImages]);

  /* ---------------- UI States ---------------- */
  if (loading || imgLoading || !imagesReady) {
    return <p>Preparing PDF...</p>;
  }

  if (!images.length) {
    return <p>No images available for PDF</p>;
  }

  return (
    <DownloadPdfButton fileName={`${inspection?.inspectionId}.pdf`} inspectionId={inspection?.inspectionId}>
      {/* ✅ TEXT DATA */}
      <DamagePdf
        response={filteredDamages}
        inspectionId={inspection?.inspectionId || ''}
        vinId={inspection?.vin || ''}
        podId={inspection?.terminal || ''}
        systemDate={inspection?.dateTime || ''}
        terminalName={inspection?.location || ''}
        make={inspection?.make || ''}
        model={inspection?.model || ''}
        year={inspection?.year || ''}
      />

      {/* ✅ IMAGE DATA */}
      <DamageImagePdf response={images} inspectionId={inspection?.inspectionId || ''} />
    </DownloadPdfButton>
  );
};

export default PdfContainer;
